import React, { useState, useEffect } from 'react';
import { useWorkspaceRoster } from '../../hooks/useWorkspaceRoster';
import WorkflowEditor from '../WorkflowEditor';

const NAVY   = '#0C2D48';
const BORDER = '#e0e0e0';
const BG     = '#f8f9fb';
const FONT   = "'Raleway', sans-serif";

const COLORS = [
  '#5352ed', '#27ae60', '#e74c3c', '#f39c12',
  '#0C2D48', '#8e44ad', '#16a085', '#d35400',
  '#2980b9', '#c0392b', '#27ae60', '#E4E69B',
];

const INTERNAL_CATEGORIES = [
  'Admin', 'Finance', 'HR/Team', 'Marketing/Socials',
  'Operations', 'Product', 'Other',
];

const INPUT = {
  width: '100%', padding: '9px 12px',
  border: `1px solid ${BORDER}`, borderRadius: 8,
  fontSize: 14, boxSizing: 'border-box',
  outline: 'none', background: '#fff',
  fontFamily: FONT, color: '#111',
};

const LABEL = {
  display: 'block', fontSize: 11, fontWeight: 700,
  color: '#6b7280', marginBottom: 5,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  fontFamily: FONT,
};

function Field({ label, children }) {
  return (
    <div>
      <label style={LABEL}>{label}</label>
      {children}
    </div>
  );
}

export default function CreateProjectModal({ isOpen, onClose, onSave, editProject = null }) {
  const { members: allUsers } = useWorkspaceRoster();

  // Which type tab is active
  const [projectTab, setProjectTab] = useState('client');

  const EMPTY = {
    name: '', client: '', status: 'active',
    color: COLORS[0], deadline: '', startDate: '',
    budget: '', description: '', team: [],
    projectType: 'client', category: '',
  };

  const [form, setForm]   = useState(EMPTY);
  const [error, setError] = useState('');
  const [customCat, setCustomCat] = useState('');
  const [workflowDraft, setWorkflowDraft] = useState({ stages: [], columns: [] });

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setWorkflowDraft({ stages: [], columns: [] });
    if (editProject) {
      const type = editProject.projectType || editProject.project_type || 'client';
      setProjectTab(type);
      setForm({
        name:        editProject.name        || '',
        client:      editProject.client      || '',
        status:      editProject.status      || 'active',
        color:       editProject.color       || COLORS[0],
        deadline:    editProject.deadline    || '',
        startDate:   editProject.startDate   || editProject.start_date || '',
        budget:      editProject.budget != null ? String(editProject.budget) : '',
        description: editProject.description || '',
        team:        editProject.team        || [],
        projectType: type,
        category:    editProject.category    || '',
      });
    } else {
      setForm(EMPTY);
      setProjectTab('client');
    }
    setCustomCat('');
  }, [isOpen, editProject]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const toggleMember = (id) => {
    set('team', form.team.includes(id)
      ? form.team.filter(x => x !== id)
      : [...form.team, id]
    );
  };

  const handleTabSwitch = (tab) => {
    setProjectTab(tab);
    set('projectType', tab);
    // Clear cross-type fields
    if (tab === 'internal') set('client', '');
    if (tab === 'client')   set('category', '');
  };

  const handleSave = () => {
    if (!form.name.trim()) { setError('Project name is required.'); return; }
    const category = projectTab === 'internal'
      ? (customCat.trim() || form.category || 'Other')
      : '';
    onSave({
      name:          form.name.trim(),
      client:        projectTab === 'client' ? form.client.trim() : '',
      status:        form.status,
      color:         form.color,
      deadline:      form.deadline,
      startDate:     form.startDate,
      budget:        form.budget ? parseFloat(form.budget) : null,
      description:   form.description.trim(),
      team:          form.team,
      projectType:   projectTab,
      category,
      workflowDraft,
    });
    onClose();
  };

  const isInternal = projectTab === 'internal';

  // Colour picker row
  const ColourPicker = () => (
    <Field label="Colour">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {COLORS.map(c => (
          <button key={c} type="button" onClick={() => set('color', c)}
            style={{
              width: 28, height: 28, borderRadius: '50%', background: c,
              border: form.color === c ? `3px solid ${NAVY}` : '3px solid transparent',
              cursor: 'pointer', boxSizing: 'border-box', flexShrink: 0,
            }}
          />
        ))}
      </div>
    </Field>
  );

  // Team member picker
  const TeamPicker = () => (
    <Field label="Team Members">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {allUsers.map(u => {
          const selected = form.team.includes(u.id);
          return (
            <button key={u.id} type="button" onClick={() => toggleMember(u.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 20,
                border: `2px solid ${selected ? u.color : BORDER}`,
                background: selected ? `${u.color}18` : '#fff',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
                color: selected ? u.color : '#555',
                fontFamily: FONT,
              }}
            >
              <span style={{
                width: 20, height: 20, borderRadius: '50%', background: u.color,
                color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {u.name.charAt(0)}
              </span>
              {u.name}
            </button>
          );
        })}
      </div>
    </Field>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#fff', borderRadius: 16,
        width: 540, maxWidth: '95vw', maxHeight: '92vh',
        overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        fontFamily: FONT,
      }}>
        {/* Modal header */}
        <div style={{
          padding: '20px 26px 16px',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: NAVY, fontFamily: "'Poppins', sans-serif" }}>
            {editProject ? 'Edit Project' : 'New Project'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999', lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Project type tabs */}
        {!editProject && (
          <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER}`, background: '#fafafa' }}>
            {[
              { key: 'client',   icon: '💼', label: 'Client Project'   },
              { key: 'internal', icon: '📁', label: 'Internal Project' },
            ].map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabSwitch(tab.key)}
                style={{
                  flex: 1, padding: '13px 0',
                  background: 'none', border: 'none',
                  borderBottom: projectTab === tab.key ? `2.5px solid ${NAVY}` : '2.5px solid transparent',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 13, fontWeight: projectTab === tab.key ? 700 : 500,
                  color: projectTab === tab.key ? NAVY : '#888',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6, marginBottom: -1,
                }}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Form body */}
        <div style={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <p style={{ margin: 0, color: '#E96A6A', fontSize: 13, fontWeight: 600 }}>{error}</p>}

          {/* Project name */}
          <Field label="Project Name *">
            <input
              style={INPUT} autoFocus
              value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Brand Refresh"
            />
          </Field>

          {/* Client-specific: client name */}
          {!isInternal && (
            <Field label="Client">
              <input
                style={INPUT}
                value={form.client} onChange={e => set('client', e.target.value)}
                placeholder="Client name or company"
              />
            </Field>
          )}

          {/* Internal-specific: category */}
          {isInternal && (
            <Field label="Category">
              <select
                style={INPUT}
                value={form.category}
                onChange={e => {
                  set('category', e.target.value);
                  if (e.target.value !== '__custom__') setCustomCat('');
                }}
              >
                <option value="">Select category…</option>
                {INTERNAL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__custom__">Other (type below)…</option>
              </select>
              {(form.category === '__custom__' || (form.category === '' && customCat)) && (
                <input
                  style={{ ...INPUT, marginTop: 8 }}
                  value={customCat}
                  onChange={e => setCustomCat(e.target.value)}
                  placeholder="Custom category name…"
                  autoFocus
                />
              )}
            </Field>
          )}

          {/* Status */}
          <Field label="Status">
            <select style={INPUT} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="on hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </Field>

          {/* Start date + Due date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Start Date">
              <input type="date" style={INPUT} value={form.startDate} onChange={e => set('startDate', e.target.value)} />
            </Field>
            <Field label="Due Date">
              <input type="date" style={INPUT} value={form.deadline} onChange={e => set('deadline', e.target.value)} />
            </Field>
          </div>

          {/* Budget (client only) */}
          {!isInternal && (
            <Field label="Budget (£) — optional">
              <input
                type="number" style={INPUT} min="0"
                value={form.budget} onChange={e => set('budget', e.target.value)}
                placeholder="0"
              />
            </Field>
          )}

          {/* Description */}
          <Field label="Description">
            <textarea
              style={{ ...INPUT, minHeight: 68, resize: 'vertical' }}
              value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="What is this project about?"
            />
          </Field>

          {/* Colour */}
          <ColourPicker />

          {/* Team */}
          {allUsers.length > 0 && <TeamPicker />}

          {/* Workflow — only on create (not edit) */}
          {!editProject && (
            <div>
              <label style={LABEL}>Workflow</label>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280', fontFamily: FONT }}>
                Define how work moves through this project
              </p>
              <WorkflowEditor
                mode="project"
                entityId={null}
                showColumns={true}
                showAutomations={false}
                onChange={setWorkflowDraft}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 26px',
          borderTop: `1px solid ${BORDER}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            type="button" onClick={onClose}
            style={{
              padding: '9px 20px', borderRadius: 8,
              border: `1px solid ${BORDER}`, background: '#fff',
              cursor: 'pointer', fontSize: 14, color: '#555', fontFamily: FONT,
            }}
          >
            Cancel
          </button>
          <button
            type="button" onClick={handleSave}
            style={{
              padding: '9px 22px', borderRadius: 8, border: 'none',
              background: NAVY, color: '#fff', cursor: 'pointer',
              fontSize: 14, fontWeight: 700, fontFamily: FONT,
            }}
          >
            {editProject ? 'Save Changes' : isInternal ? 'Create Internal Project' : 'Create Client Project'}
          </button>
        </div>
      </div>
    </div>
  );
}
